/**
 * Acquisition pricing, negotiation outcomes, driver retention, asset transfer.
 * All pure functions — the Zustand slice composes them.
 *
 * Pricing formula (chosen by user — option 2c):
 *   askingPrice = 0.5 × CA_annuel + 0.8 × flotte_resale + 1.2 × contrats_actifs_valeur + dette_nette
 *
 * Negotiation outcomes are gated by reputation + the dominant weakness leverage.
 */
import { VEHICLES } from '../data/vehicles';

const VEHICLE_AVG_RESALE = 35000;   // €/véhicule (proxy)
const CONTRACT_AVG_VALUE = 4500;    // € de valeur restante par contrat actif

/**
 * Asking price proposed by the DAF.
 */
export function computeAskingPrice(analysis) {
  if (!analysis || analysis.locked) return 0;
  const caAnnual = (analysis.ca || 0) * 12;
  const fleetValue = (analysis.fleet || 0) * VEHICLE_AVG_RESALE;
  const contractsValue = (analysis.contractsActive || 0) * CONTRACT_AVG_VALUE;
  const netDebt = analysis.netDebt || 0;
  return Math.round(0.5 * caAnnual + 0.8 * fleetValue + 1.2 * contractsValue + netDebt);
}

/**
 * Apply negotiation: returns { offerPrice, successChance, isAccepted }.
 * The user picks one of 3 strategies:
 *   - 'accept'      : pay asking price (100% success)
 *   - 'negotiate'   : offer asking × 0.85 (-15%)
 *   - 'aggressive'  : offer asking × 0.75 (-25%)
 *
 * Success modifiers:
 *   - Player reputation:    +0.4 × (reputation/100)
 *   - Weakness leverage:    +leverage (0.03 to 0.05 typically)
 *   - Difficulty offset:    base 0.55 (negotiate) / 0.30 (aggressive)
 *
 * Returns a deterministic outcome only if `roll` is provided (for tests).
 */
export function evaluateNegotiation(askingPrice, strategy, { reputation = 50, weaknessLeverage = 0, roll }) {
  const repBonus = (reputation / 100) * 0.4;
  let base, discount;
  if (strategy === 'accept') {
    base = 1.0; discount = 0;
  } else if (strategy === 'negotiate') {
    base = 0.55; discount = 0.15;
  } else if (strategy === 'aggressive') {
    base = 0.30; discount = 0.25;
  } else {
    return { offerPrice: askingPrice, successChance: 1, isAccepted: true, strategy: 'accept' };
  }
  const successChance = Math.max(0, Math.min(1, base + repBonus + weaknessLeverage));
  const r = (typeof roll === 'number') ? roll : Math.random();
  const isAccepted = r < successChance;
  const finalPrice = isAccepted
    ? Math.round(askingPrice * (1 - discount))
    : askingPrice; // refus → l'acheteur paye le prix demandé
  return {
    offerPrice: finalPrice,
    askingPrice,
    successChance,
    isAccepted,
    strategy,
    discount: isAccepted ? discount : 0,
  };
}

/**
 * Simulate driver retention after the acquisition closes (option 4c).
 *
 * For each driver of the acquired competitor:
 *   - satisfaction > 60 → 90 % stay
 *   - 40-60            → 60 % stay
 *   - < 40             → 25 % stay
 * Modifier: + (HST.reputation - 50) / 100 × 0.20 (good rep keeps more drivers)
 *
 * @param {Array}  drivers           — competitor's drivers
 * @param {number} hstReputation     — player reputation (0-100)
 * @param {Function} rng             — for tests, pass () => 0.5 to lock outcomes
 */
export function simulateDriverRetention(drivers, hstReputation = 50, rng = Math.random) {
  const repModifier = ((hstReputation - 50) / 100) * 0.20;
  const stayed = [];
  const left = [];
  (drivers || []).forEach(d => {
    let baseStay;
    const sat = d.satisfaction || 50;
    if (sat > 60) baseStay = 0.90;
    else if (sat >= 40) baseStay = 0.60;
    else baseStay = 0.25;
    const stayChance = Math.max(0, Math.min(1, baseStay + repModifier));
    if (rng() < stayChance) stayed.push(d);
    else left.push(d);
  });
  return { stayed, left, stayRate: drivers && drivers.length ? stayed.length / drivers.length : 0 };
}

/**
 * Generate a synthetic competitor roster (drivers) for the M&A flow.
 * Real game state doesn't track each competitor's drivers individually —
 * we mint them at acquisition time from the analysis numbers, with realistic
 * names + a satisfaction distribution.
 *
 * Deterministic given the competitor.id (uses simple PRNG seeded from id).
 */
/**
 * Compute a baseline satisfaction score from the competitor's personality.
 * Replaces the nested ternary called out by code review (28/05/2026).
 */
function _baselineSatFromPersonality(personality) {
  if (personality === 'agressif') return 45;
  if (personality === 'ecolo')    return 70;
  return 60;
}

/**
 * Compute a driver's seniority label from its index in the cohort.
 * Replaces the nested ternary `i < count/3 ? 'Junior' : i < count*2/3 ? ...`.
 *
 * 17/06/2026 — Retour utilisateur : « level: 'Junior' (string) cassait le
 * dashboard RH (NaN niveau moyen, NaN XP) et bloquait silencieusement la
 * formation des chauffeurs rachetés ». Le système interne fonctionne en
 * numérique 1-5 (cf. LEVEL_BONUSES). On mappe désormais :
 *   - Junior   → niveau 1 (Débutant)
 *   - Confirmé → niveau 3 (Expérimenté)
 *   - Sénior   → niveau 4 (Senior)
 * Le rachat n'octroie plus de niveau 5 (réservé au mérite intra-jeu).
 */
function _levelFromCohortIndex(index, total) {
  if (index < total / 3) return 1;       // Junior
  if (index < (total * 2) / 3) return 3; // Confirmé
  return 4;                              // Sénior
}

// Pool of portrait IDs (matching files in /public/driver-portraits/) used to
// give acquired drivers a real face + name + bio. The `dataId` field on each
// generated driver points to this pool; the unique `id` still uses the
// `{competitor.id}-drv-{i+1}` convention so multiple acquisitions never
// collide.
const ACQUIRED_PORTRAIT_POOL = [
  { dataId: 'alexandre-moreau',   name: 'Alexandre Moreau',  age: 47, bioKey: 'acq_drv_bio_veteran' },
  { dataId: 'amina-toure',        name: 'Amina Touré',       age: 31, bioKey: 'acq_drv_bio_quiet_pro' },
  { dataId: 'aurelie-bernard',    name: 'Aurélie Bernard',   age: 36, bioKey: 'acq_drv_bio_methodical' },
  { dataId: 'camille-roux',       name: 'Camille Roux',      age: 33, bioKey: 'acq_drv_bio_team_player' },
  { dataId: 'chantal-perrin',     name: 'Chantal Perrin',    age: 52, bioKey: 'acq_drv_bio_old_school' },
  { dataId: 'dylan-moreaux',      name: 'Dylan Moreaux',     age: 26, bioKey: 'acq_drv_bio_eager_youngster' },
  { dataId: 'eva-laurent',        name: 'Eva Laurent',       age: 29, bioKey: 'acq_drv_bio_quiet_pro' },
  { dataId: 'fatou-diallo',       name: 'Fatou Diallo',      age: 34, bioKey: 'acq_drv_bio_methodical' },
  { dataId: 'hassan-el-khoury',   name: 'Hassan El Khoury',  age: 45, bioKey: 'acq_drv_bio_diplomat' },
  { dataId: 'helene-vasseur',     name: 'Hélène Vasseur',    age: 49, bioKey: 'acq_drv_bio_veteran' },
  { dataId: 'isabelle-morin',     name: 'Isabelle Morin',    age: 41, bioKey: 'acq_drv_bio_team_player' },
  { dataId: 'jean-baptiste-rey',  name: 'Jean-Baptiste Rey', age: 38, bioKey: 'acq_drv_bio_methodical' },
  { dataId: 'jerome-castillo',    name: 'Jérôme Castillo',   age: 44, bioKey: 'acq_drv_bio_diplomat' },
  { dataId: 'karim-belhadj',      name: 'Karim Belhadj',     age: 32, bioKey: 'acq_drv_bio_eager_youngster' },
  { dataId: 'laurent-dubois',     name: 'Laurent Dubois',    age: 50, bioKey: 'acq_drv_bio_old_school' },
  { dataId: 'lucia-santos',       name: 'Lúcia Santos',      age: 37, bioKey: 'acq_drv_bio_quiet_pro' },
  { dataId: 'marco-ferreira',     name: 'Marco Ferreira',    age: 39, bioKey: 'acq_drv_bio_team_player' },
  { dataId: 'miriam-schneider',   name: 'Miriam Schneider',  age: 46, bioKey: 'acq_drv_bio_veteran' },
  { dataId: 'mohamed-ait-ali',    name: 'Mohamed Aït Ali',   age: 35, bioKey: 'acq_drv_bio_methodical' },
  { dataId: 'nadia-bensalem',     name: 'Nadia Bensalem',    age: 30, bioKey: 'acq_drv_bio_eager_youngster' },
  { dataId: 'pierre-yves-gall',   name: 'Pierre-Yves Gall',  age: 51, bioKey: 'acq_drv_bio_old_school' },
  { dataId: 'rachid-hammami',     name: 'Rachid Hammami',    age: 43, bioKey: 'acq_drv_bio_diplomat' },
  { dataId: 'sebastien-leclerc',  name: 'Sébastien Leclerc', age: 40, bioKey: 'acq_drv_bio_methodical' },
  { dataId: 'sylvie-gauthier',    name: 'Sylvie Gauthier',   age: 48, bioKey: 'acq_drv_bio_veteran' },
  { dataId: 'thibaut-renard',     name: 'Thibaut Renard',    age: 28, bioKey: 'acq_drv_bio_eager_youngster' },
  { dataId: 'tom-marchand',       name: 'Tom Marchand',      age: 27, bioKey: 'acq_drv_bio_team_player' },
  { dataId: 'youssef-el-amrani',  name: 'Youssef El Amrani', age: 36, bioKey: 'acq_drv_bio_quiet_pro' },
];

/**
 * Generate the drivers acquired from a competitor.
 *
 * 02/06/2026 — `excludeDataIds` parameter added : the caller must pass
 * the set of `dataId`s already present in the player's roster (active
 * drivers + firedDriversArchive). Acquired drivers will skip any
 * portrait matching that set so we never create a runtime duplicate
 * (player feedback : « certains des chauffeurs récupérés sont des
 * doublons de ceux disponible dans le pool initial du joueur »).
 */
export function generateCompetitorDrivers(competitor, analysis, rng = Math.random, excludeDataIds = null) {
  const count = analysis.drivers || 0;
  const baseSat = _baselineSatFromPersonality(competitor.personality);
  const drivers = [];
  // Shuffle the portrait pool deterministically so the same competitor seed
  // gives the same cast — and so we never assign the same face twice in one
  // M&A operation (when the count is below pool length).
  let pool = [...ACQUIRED_PORTRAIT_POOL];
  // 02/06/2026 — Exclude any dataId already present in the player's
  // roster (active or fired). The portrait pool is large enough that
  // even after this filter we have ≥ 10 candidates for the heaviest
  // acquisitions (analysis.drivers caps at ~12 in practice).
  if (excludeDataIds && typeof excludeDataIds.has === 'function') {
    const filtered = pool.filter(p => !excludeDataIds.has(p.dataId));
    // Defensive : if filtering would leave us with fewer portraits than
    // we need, keep the unfiltered pool — better to risk a visual
    // duplicate than to fail the acquisition entirely.
    if (filtered.length >= count) pool = filtered;
  }
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  for (let i = 0; i < count; i++) {
    const persona = pool[i % pool.length];
    const jitter = (rng() - 0.5) * 30;
    const level = _levelFromCohortIndex(i, count);
    const employerCost = 2400 + Math.floor(rng() * 800);
    // 17/06/2026 — XP cohérent avec le niveau (palier précédent + 0-40 %
    // du delta vers le palier suivant). Avant : Math.floor(rng()*1500)
    // affichait "1495 / NaN XP" car level était string + xp non aligné.
    const _XP_THRESHOLDS = [0, 600, 1800, 3600, 6000]; // 1→2, 2→3, 3→4, 4→5
    const _lowerXp = _XP_THRESHOLDS[level - 1] || 0;
    const _upperXp = _XP_THRESHOLDS[level] || _lowerXp + 800;
    const _xp = Math.floor(_lowerXp + rng() * (_upperXp - _lowerXp) * 0.4);
    drivers.push({
      id: `${competitor.id}-drv-${i + 1}`,
      // dataId points the portrait helper at the right file in /public.
      dataId: persona.dataId,
      name: persona.name,
      age: persona.age,
      bioKey: persona.bioKey,
      level,
      satisfaction: Math.max(15, Math.min(95, Math.round(baseSat + jitter))),
      employerCost,
      competence: 50 + Math.floor(rng() * 40),
      xp: _xp,
      totalTrips: Math.floor(rng() * 80),
      assignedVehicleId: null,
      assignedContractId: null,
      onMission: false,
      hiredOn: 0,
      acquiredFromCompetitorId: competitor.id,
      // 13/06/2026 — Bug fix « fatigue buguée post-rachat » : les chauffeurs
      // générés n'avaient ni fatigue, ni statut, ni salaire brut, ni permis.
      // La jauge fatigue affichait NaN et le tick quotidien propageait des
      // undefined. Tous les champs du modèle chauffeur sont désormais posés.
      fatigue: 5 + Math.floor(rng() * 25),
      fatigueMax: 80,
      status: 'idle',
      salaryGross: Math.round(employerCost / 1.42),
      trainings: [],
      // 17/06/2026 — Retour user M&A : « Les chauffeurs n'ont pas de
      // spécialisations, ni de permis particuliers. Il en faudrait. »
      // On hérite désormais des qualifications du concurrent, pondérées
      // par le niveau (un senior expérimenté arrive avec une spé, un junior
      // a plus de chances d'arriver sans). Le permis est déjà géré plus bas.
      extraSpecialties: _pickDriverSpecialties(competitor.specialties, level, rng),
      // Retour utilisateur « 8 mecs pas trop qualifiés avec uniquement
      // permis B » : les seniors du rachat arrivent désormais avec les
      // permis cohérents avec la flotte qu'ils conduisaient.
      licenses: level >= 4 ? ['B', 'C', 'CE'] : level >= 2 ? ['B', 'C'] : ['B'],
      onHoliday: false,
      holidayStartDay: null,
      holidayEndDay: null,
    });
  }
  return drivers;
}

/**
 * Pick a coherent set of `extraSpecialties` for an acquired driver.
 * - Higher-level drivers (seniors) are more likely to inherit a specialty
 *   from the competitor's portfolio (chosen at random).
 * - L1 (juniors) : 20 % chance to inherit ONE specialty.
 * - L2-L3 (médians) : 55 % chance to inherit ONE specialty.
 * - L4-L5 (seniors) : 90 % chance to inherit ONE specialty, with a 30 %
 *   bonus chance to know a SECOND one (équipe polyvalente).
 *
 * Falls back to an empty array if the competitor has no specialties.
 */
function _pickDriverSpecialties(competitorSpecs, level, rng) {
  // 22/07/2026 — Retour user M&A : « Il faudrait a minima une qualification
  // pour tous les niveaux 1, 2 qualifications pour les niveaux 2 et 3,
  // 3 qualifications pour les niveaux 4. » Planchers GARANTIS (plus de
  // probabilités) : les spécialités du concurrent sont prioritaires, le
  // complément vient du référentiel canonique des qualifications chauffeur.
  const lvl = Number.isFinite(level) ? level : 1;
  const target = lvl >= 4 ? 3 : lvl >= 2 ? 2 : 1;
  const primary = [...new Set(
    (competitorSpecs || []).map(_normalizeDriverSpec).filter(s => _CANON_DRIVER_SPECS.includes(s))
  )];
  const rest = _CANON_DRIVER_SPECS.filter(s => !primary.includes(s));
  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };
  return [...shuffle(primary), ...shuffle(rest)].slice(0, target);
}

// Référentiel canonique des qualifications chauffeur (celui des formations
// et de la compatibilité contrats).
const _CANON_DRIVER_SPECS = [
  'Messagerie/Colis', 'Frigo/Pharma', 'BTP/Matériaux/Engins TP',
  'ADR/Produits sensibles', 'Export UE', 'Vrac/Palettes',
];

// Les concurrents déclarent leurs spécialités avec des libellés variables
// ('Messagerie / Colis', 'BTP / Matériaux', 'Messagerie express'…) : on les
// rabat sur le canon pour que les qualifications héritées soient reconnues
// par la compatibilité contrats.
const _DRIVER_SPEC_ALIASES = {
  'Messagerie / Colis': 'Messagerie/Colis',
  'Messagerie': 'Messagerie/Colis',
  'Messagerie express': 'Messagerie/Colis',
  'BTP / Matériaux': 'BTP/Matériaux/Engins TP',
  'BTP': 'BTP/Matériaux/Engins TP',
  'Vrac / Palettes': 'Vrac/Palettes',
  'ADR': 'ADR/Produits sensibles',
  'Export EU': 'Export UE',
};
function _normalizeDriverSpec(s) {
  return _DRIVER_SPEC_ALIASES[s] || s;
}

/**
 * 13/06/2026 — Transfert des contrats en cours lors d'un rachat.
 * Retour utilisateur : « il n'y a pas de transfert des contrats en cours
 * [...] Je ne trouve pas de croissance externe dans cet achat. »
 *
 * Le prix demandé (computeAskingPrice) valorise déjà `contractsActive`
 * (× 1.2 CONTRACT_AVG_VALUE) — il est donc normal que l'acheteur récupère
 * une partie du portefeuille. On transfère 1 à 3 contrats réguliers,
 * directement assignés aux paires véhicule + chauffeur reprises (la
 * continuité d'exploitation, comme dans un vrai M&A).
 *
 * MUTATES the passed vehicles/drivers entries (status → active + liens),
 * same pattern as the depotId assignment in completeAcquisition.
 */
const INHERITED_CONTRACT_POOL = [
  { name: 'Distribution régionale (repris)', category: 'Vrac / Palettes' },
  { name: 'Tournée logistique (repris)', category: 'Messagerie / Colis' },
  { name: 'Navette industrielle (repris)', category: 'Vrac / Palettes' },
  { name: 'Flux GMS quotidien (repris)', category: 'Messagerie / Colis' },
];
const _INHERITED_DAILY_BY_TIER = { 1: 190, 2: 330, 3: 620, 4: 880, 5: 1050 };

export function generateInheritedContracts(competitor, analysis, vehicles, drivers, rng = Math.random, totalDays = 0) {
  const contractsActive = analysis?.contractsActive || 0;
  if (contractsActive < 1) return [];
  const freeVehicles = (vehicles || []).filter(v => v.status === 'idle');
  const freeDrivers = (drivers || []).filter(d => d.status === 'idle');
  const pairs = Math.min(freeVehicles.length, freeDrivers.length);
  const count = Math.min(3, Math.max(1, Math.round(contractsActive / 4)), pairs);
  const contracts = [];
  for (let i = 0; i < count; i++) {
    const vehicle = freeVehicles[i];
    const driver = freeDrivers[i];
    const template = INHERITED_CONTRACT_POOL[i % INHERITED_CONTRACT_POOL.length];
    const baseDaily = _INHERITED_DAILY_BY_TIER[vehicle.tier] || 300;
    const dailyRevenue = Math.round(baseDaily * (0.85 + rng() * 0.30));
    const daysRemaining = 30 + Math.floor(rng() * 31); // 30-60 j restants
    // Liens d'exploitation (continuité) — mutation volontaire des objets
    // fraîchement générés, avant leur commit dans le store.
    vehicle.status = 'active';
    vehicle.assignedDriverId = driver.id;
    driver.status = 'active';
    driver.assignedVehicleId = vehicle.id;
    const instanceId = `${competitor.id}-ctr-${i + 1}-${totalDays}`;
    vehicle.assignedContractId = instanceId;
    contracts.push({
      id: `inherited-${competitor.id}-${i + 1}`,
      instanceId,
      name: template.name,
      category: template.category,
      frequency: 'régulier',
      tierMin: vehicle.tier,
      dailyRevenue,
      revenue: dailyRevenue,
      totalValue: dailyRevenue * 60,
      duration: 60,
      daysRemaining,
      vehicleId: vehicle.id,
      driverId: driver.id,
      clientName: `Clientèle ${competitor.name}`,
      inherited: true,
      inheritedFrom: competitor.id,
      spawnDay: totalDays,
      startDay: totalDays,
    });
  }
  return contracts;
}

/**
 * Pick the brand list for a competitor's fleet. Same purpose as the nested
 * ternary the code review flagged — extracted for readability.
 */
// 22/07/2026 — les anciens helpers de flotte synthétique (marques fictives
// Scanex/Valrek/Volvax, prix/capacité/carburant par tier, numéro de modèle
// aléatoire) ont été supprimés : la flotte reprise vient du catalogue réel.

/**
 * Pick the tier distribution for a competitor's fleet based on game-phase
 * + competitor personality.
 *
 * 02/06/2026 — Concurrent evolution (player feedback) : « les concurrents
 * doivent suivre une évolution proche du joueur ». We escalate the tier
 * mix as years roll by so late-game acquisitions remain juicy (tier 4-5
 * vehicles in years 3+).
 *
 *   • Year 1 (yearsPlayed = 0)  : [1, 2, 2, 3]  (status quo)
 *   • Year 2                    : [2, 2, 3, 3]
 *   • Year 3                    : [2, 3, 3, 4]
 *   • Year 4                    : [3, 3, 4, 4]
 *   • Year 5+                   : [3, 4, 4, 5]
 *
 * Aggressive competitors lean one notch higher (top-shelf truckers).
 * Ecolo competitors stay one notch lower (smaller / cleaner fleet).
 */
function _tierDistributionForPhase(personality, yearsPlayed = 0) {
  const ladders = [
    [1, 2, 2, 3],
    [2, 2, 3, 3],
    [2, 3, 3, 4],
    [3, 3, 4, 4],
    [3, 4, 4, 5],
  ];
  const idx = Math.min(ladders.length - 1, Math.max(0, Math.floor(yearsPlayed)));
  const base = ladders[idx];
  if (personality === 'agressif') {
    return base.map(t => Math.min(5, t + 1));
  }
  if (personality === 'ecolo') {
    return base.map(t => Math.max(1, t - 1));
  }
  return base;
}

/**
 * Per-tier compatibility map — which specialties make pedagogical sense
 * for each vehicle tier. Used by `generateCompetitorVehicles` to avoid
 * absurd combinations (e.g. T1 fourgon léger avec « Export EU » alors
 * que la tier-rule de `vehicleAccessibility.js` lui interdit explicite-
 * ment ce type de contrat).
 *
 * 17/06/2026 — Retour utilisateur (M&A) : « les camions ont tous la spé
 * EU Export, au lieu d'être hétéroclytes. Incohérence T2 Export ». Désormais
 * on filtre la liste des specialties du concurrent selon le tier ET on
 * distribue de manière hétérogène (pas tous la première).
 */
const _TIER_COMPATIBLE_SPECIALTIES = {
  1: ['Messagerie', 'Messagerie express', 'Colis', 'Distribution', 'VTC léger', 'Économique', 'Messagerie / Colis', 'Messagerie/Colis'],
  2: ['Messagerie', 'Messagerie / Colis', 'Messagerie/Colis', 'Vrac', 'Vrac / Palettes', 'Vrac/Palettes', 'Palettes', 'Distribution', 'BTP', 'BTP/Matériaux/Engins TP', 'Chantier', 'Économique', 'Frigo/Pharma'],
  3: ['Vrac', 'Vrac / Palettes', 'Vrac/Palettes', 'Vrac lourd', 'BTP', 'BTP/Matériaux/Engins TP', 'Chantier', 'Longue distance', 'National', 'Palettes', 'Export UE', 'Frigo/Pharma'],
  4: ['Frigorifique', 'Frigo/Pharma', 'GMS', 'Pharma', 'ADR', 'ADR/Produits sensibles', 'Matières dangereuses', 'Spécialisé', 'Citerne', 'Export UE'],
  5: ['Export EU', 'Export UE', 'Longue distance', 'Prestige', 'BTP lourd', 'BTP/Matériaux/Engins TP', 'Engins de chantier', 'ZFE', 'Image verte', 'International', 'EU', 'Vrac/Palettes'],
};

/**
 * Pick a coherent specialty set for an acquired vehicle.
 * 22/07/2026 — SUPPRIMÉ (flotte reprise = modèles réels du catalogue, qui
 * portent leurs propres specialties). Conservé : _TIER_COMPATIBLE_SPECIALTIES
 * (utilisé par _clampTierMixToCompetitorProfile).
 */

/**
 * Clamp the tier distribution to tiers the competitor can pedagogically
 * operate. A messager-only concurrent (Messagerie / Colis, Vrac / Palettes)
 * cannot pedagogically own T5 prestige/Export trucks ; we collapse those
 * upper tiers to T3 to keep the M&A faithful to the competitor's profile.
 *
 * Si le concurrent n'a AUCUNE spécialité déclarée (tests legacy ou data
 * minimaliste), on n'applique pas le clamp — comportement historique
 * préservé.
 *
 * Returns a tier mix in which every tier is "supported" by at least one
 * of the competitor's specialties OR is generic enough (T1-T3) to need no
 * particular specialty.
 */
function _clampTierMixToCompetitorProfile(tiers, competitorSpecs) {
  const specs = competitorSpecs || [];
  if (specs.length === 0) return tiers; // pas de profil → comportement historique
  return tiers.map((t) => {
    // T1-T3 sont génériques : pas de contrainte de spécialité.
    if (t <= 3) return t;
    // T4/T5 doivent avoir au moins une spécialité du concurrent dans leur
    // bucket. Sinon on rétrograde à T3 (pas d'achat d'un porte-engins ou
    // d'un V8 prestige par un messager).
    const bucket = _TIER_COMPATIBLE_SPECIALTIES[t] || [];
    const hasMatch = specs.some(s => bucket.includes(s));
    if (hasMatch) return t;
    return 3; // fallback safe (T3 polyvalent)
  });
}

/**
 * @param {Object} competitor
 * @param {Object} analysis
 * @param {Function} [rng=Math.random]
 * @param {Object} [opts]
 * @param {number} [opts.yearsPlayed=0] — used by `_tierDistributionForPhase`
 *   to escalate the tier mix. Pass `Math.floor(state.totalDays / 360)`.
 *
 * 22/07/2026 — Retour user M&A : « il ne faut pas faire une nouvelle
 * catégorie de camions dédiés à la concurrence, cela n'apporte que des
 * confusions. Leur flotte doit être identique à la nôtre. » Les camions
 * repris sont désormais de VRAIS modèles du catalogue joueur
 * (data/vehicles.js) — mêmes noms, specs, prix, specialties — simplement
 * d'occasion (usure + km). Fini les Scanex/Valrek/Volvax « Heavy-4823 ».
 */
export function generateCompetitorVehicles(competitor, analysis, rng = Math.random, opts = {}) {
  const count = analysis.fleet || 0;
  const baseTiers = _tierDistributionForPhase(competitor.personality, opts.yearsPlayed || 0);
  // 17/06/2026 — Clamp le tier max selon le profil de spécialités du
  // concurrent (un messager ne devrait pas générer de T5 prestige).
  const tiers = _clampTierMixToCompetitorProfile(baseTiers, competitor.specialties);
  const vehicles = [];
  for (let i = 0; i < count; i++) {
    const tier = tiers[Math.floor(rng() * tiers.length)];
    const model = _pickCatalogueModel(tier, competitor.specialties, rng);
    const wear = 20 + Math.floor(rng() * 50); // used vehicles, some wear
    vehicles.push({
      // Même forme d'instance que fleetSlice.buyVehicle — la flotte reprise
      // est indiscernable d'un camion acheté par le joueur.
      id: `${competitor.id}-veh-${i + 1}`,
      dataId: model.id,
      brand: model.brand,
      model: model.model,
      tier: model.tier,
      image: model.image || null,
      specialties: model.specialties,
      price: model.price,
      maintenancePerMonth: model.maintenancePerMonth,
      insurancePerMonth: model.insurancePerMonth,
      dailyFuel: model.dailyFuel,
      engineType: model.engineType || 'diesel',
      wear,
      km: 30000 + Math.floor(rng() * 170000),
      daysActive: 0,
      totalRevenue: 0,
      status: 'idle',
      isLeased: false,
      mortgaged: false,
      assignedDriverId: null,
      assignedContractId: null,
      // 13/06/2026 — couverture cohérente avec la politique assureurs
      // (T5 premium obligatoire, T3/4 standard).
      insuranceCoverage: model.tier >= 5 ? 'premium' : 'standard',
      purchaseDay: 0,
      stateSubsidyReceived: 0,
      acquiredFrom: competitor.id, // tag for the UI
    });
  }
  return vehicles;
}

/**
 * Choisit un modèle RÉEL du catalogue pour le tier demandé, en privilégiant
 * ceux dont les spécialités recoupent le profil du concurrent. Fallback :
 * n'importe quel modèle du tier, puis tier inférieur (défensif).
 */
function _pickCatalogueModel(tier, competitorSpecs, rng) {
  const t = Math.max(1, Math.min(5, tier || 1));
  const inTier = VEHICLES.filter(v => v.tier === t);
  if (inTier.length === 0) return _pickCatalogueModel(t - 1, competitorSpecs, rng);
  const specs = (competitorSpecs || []).map(_normalizeDriverSpec);
  const matching = inTier.filter(v => (v.specialties || []).some(s => specs.includes(_normalizeDriverSpec(s))));
  const pool = matching.length > 0 ? matching : inTier;
  return pool[Math.floor(rng() * pool.length)];
}

/**
 * Helper for tests/UI: lightweight deterministic PRNG seeded from a string.
 * Returns a function that yields the same sequence each call for a given seed.
 */
export function seededRng(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return function rng() {
    h += 0x6D2B79F5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
